import { useContext, useEffect, useState } from 'react';
import { capitalizeFirstLetter, getCustomisationValue, updateCustomisationValue } from '../../../EditablePreview/EditablePreview';
import ModuleNumericalInput from '../../components/ModuleNumericalInput';
import './TypographyModule.scss';
import { changeNumericalFieldValue, changeStringFieldValue } from '../../ModuleList';
import ModuleTextInput from '../../components/ModuleTextInput';
import { ShowColorPicker } from '../../../../../../forms/ColorPicker/ColorPickerPopUp';
import { CampaignContext } from "../../../../../../../contexts/CampaignContext";
import { WORKSPACE_MODULE_ICONS } from '../../../../WorkspaceAssets/Assets';
import { COLOUR_NAME_TO_COLOUR_HEX } from '../../../../../../../helpers/Colours';
import AddButton from '../../../AddButton/AddButton';
import { renderFirstCustomBlock, renderSecondCustomBlock } from '../../customFields/CustomFieldRenderer';
import { slideOutPanelControl } from '../../../../../../../containers/TimelineEditor/slideout-panel/SlideoutPanel';
import ColorPickerField from '../../customFields/colorPicker/ColorPickerField';

const TypographyModule = (props) => {
    const moduleConfig = props.moduleConfig;
    const customisationVars = props.customisationVars;

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const fontFamilyFieldConfig = moduleConfig.fields.find(field => field.field.includes('typography-font'));
    const fontFaceConfig = fontFamilyFieldConfig?.["customisation-module-typography-font-face"];
    const fontWeightConfig = fontFamilyFieldConfig?.["customisation-module-typography-font-weight"];

    const fontSizeConfig = moduleConfig.fields.find(field => field.field.includes('typography-size'));
    const fontLineHeightConfig = moduleConfig.fields.find(field => field.field.includes('typography-line-height'));

    const fontAlignConfig = moduleConfig.fields.find(field => field.field.includes('typography-text-alignment'));
    const horizontalAlignConfig = fontAlignConfig?.["customisation-module-typography-text-alignment.horizontal"];
    const verticalAlignConfig = fontAlignConfig?.["customisation-module-typography-text-alignment.vertical"];

    const fontColorConfig = moduleConfig.fields.find(field => field.field.includes('typography-text-color'));

    const findFontWeightValueToDisplay = (fontWeightConfig) => {
        // Font weight is not as simple as the others, so we handle it separately
        let currentFontWeight = '-';
        if (fontWeightConfig["customisation-module-typography-font-weight-bold"].value && getCustomisationValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-bold"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === fontWeightConfig["customisation-module-typography-font-weight-bold"].value?.toLowerCase()) {
            currentFontWeight = 'Bold';
        }
        else if (fontWeightConfig["customisation-module-typography-font-weight-narrow"].value && getCustomisationValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-narrow"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === fontWeightConfig["customisation-module-typography-font-weight-narrow"].value?.toLowerCase()) {
            currentFontWeight = 'Narrow';
        }
        else if (fontWeightConfig["customisation-module-typography-font-weight-normal"].value && getCustomisationValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-normal"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === fontWeightConfig["customisation-module-typography-font-weight-normal"].value?.toLowerCase()) {
            currentFontWeight = 'Normal';
        }
        return currentFontWeight;
    }

    const [currentFontFamily, setCurrentFontFamily] = useState(getCustomisationValue(customisationVars, fontFaceConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) || '');
    const [currentFontWeight, setCurrentFontWeight] = useState(findFontWeightValueToDisplay(fontWeightConfig));
    const [currentFontSize, setCurrentFontSize] = useState(getCustomisationValue(customisationVars, fontSizeConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) || '');
    const [currentFontLineHeight, setCurrentFontLineHeight] = useState(getCustomisationValue(customisationVars, fontLineHeightConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) || 'auto');
    // const [currentHorizontalAlign, setCurrentHorizontalAlign] = useState(getCustomisationValue(customisationVars, horizontalAlignConfig?.dataLink) || '');
    // const [currentVerticalAlign, setCurrentVerticalAlign] = useState(getCustomisationValue(customisationVars, verticalAlignConfig?.dataLink) || '');
    /* const [currentFontColor, setCurrentFontColor] = useState(getCustomisationValue(customisationVars, fontColorConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) || '');
    useEffect(() => {
        slideOutPanelControl.refreshColourPicker(currentFontColor, fontColorConfig);
    }, [currentFontColor]);
    const [prevFontColorValue, setPrevFontColorValue] = useState(currentFontColor); */

    const fontWeightOptions = [];
    if (fontWeightConfig["customisation-module-typography-font-weight-narrow"].value) {
        fontWeightOptions.push({ label: 'Narrow', value: 'Narrow' });
    }
    if (fontWeightConfig["customisation-module-typography-font-weight-normal"].value) {
        fontWeightOptions.push({ label: 'Normal', value: 'Normal' });
    }
    if (fontWeightConfig["customisation-module-typography-font-weight-bold"].value) {
        fontWeightOptions.push({ label: 'Bold', value: 'Bold' });
    }

    // Vertical and horizontal aligment use the same setup as the font weight
    // Horizontal should be left, center or right
    let currentHorizontalAlign = '-';
    if (horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"].value && getCustomisationValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"].value?.toLowerCase()) {
        currentHorizontalAlign = 'left';
    }
    else if (horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"].value && getCustomisationValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"].value?.toLowerCase()) {
        currentHorizontalAlign = 'center';
    }
    else if (horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"].value && getCustomisationValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"].value?.toLowerCase()) {
        currentHorizontalAlign = 'right';
    }
    const leftAlignAvailable = !!horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"].value;
    const leftAlignValue = horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"].value;
    const centerAlignAvailable = !!horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"].value;
    const centerAlignValue = horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"].value;
    const rightAlignAvailable = !!horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"].value;
    const rightAlignValue = horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"].value;

    const horizontalAlignmentAvailable = leftAlignAvailable || centerAlignAvailable || rightAlignAvailable;

    // Vertical should be top, middle or bottom
    let currentVerticalAlign = '-';
    if (verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"].value && getCustomisationValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"].value?.toLowerCase()) {
        currentVerticalAlign = 'top';
    }
    else if (verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"].value && getCustomisationValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"].value?.toLowerCase()) {
        currentVerticalAlign = 'middle';
    }
    else if (verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"].value && getCustomisationValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)?.toString()?.toLowerCase() === verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"].value?.toLowerCase()) {
        currentVerticalAlign = 'bottom';
    }
    const topAlignAvailable = !!verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"].value;
    const middleAlignAvailable = !!verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"].value;
    const bottomAlignAvailable = !!verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"].value;

    const verticalAlignmentAvailable = topAlignAvailable || middleAlignAvailable || bottomAlignAvailable;

    const changeFontFace = (value) => {
        if (fontFaceConfig?.dataLink) {
            if (changeStringFieldValue(customisationVars, fontFaceConfig, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCurrentFontFamily(value);
                props?.forceUpdateComponent?.();
            }
        }
    }

    useEffect(() => {
        const resetDeletedFont = (event) => {
            if (event.detail?.fontFamily === currentFontFamily) {
                changeFontFace('Roboto');
            }
        };

        window.addEventListener('custom-font-deleted', resetDeletedFont);
        return () => window.removeEventListener('custom-font-deleted', resetDeletedFont);
    }, [currentFontFamily]);

    const changeFontSize = (fieldId, value) => {
        if (fontSizeConfig?.dataLink) {
            if (changeNumericalFieldValue(customisationVars, fontSizeConfig, value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCurrentFontSize(value);
                props?.forceUpdateComponent?.();
            }
        }
    }

    /* const changeFontColor = (value, isFinalSelection = true) => {
        if (fontColorConfig?.dataLink) {
            if (changeStringFieldValue(customisationVars, fontColorConfig, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode, isFinalSelection, isFinalSelection, prevFontColorValue)) {
                setCurrentFontColor(value);
                props?.forceUpdateComponent?.();
            }
        }
    } */

    const changeHorizontalAlignment = (value) => {
        switch (value) {
            case 'left':
                if (horizontalAlignConfig?.["customisation-module-typography-text-alignment-horizontal.left"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.left"], leftAlignValue, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentHorizontalAlign('left');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            case 'center':
                if (horizontalAlignConfig?.["customisation-module-typography-text-alignment-horizontal.center"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.center"], centerAlignValue, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentHorizontalAlign('center');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            case 'right':
                if (horizontalAlignConfig?.["customisation-module-typography-text-alignment-horizontal.right"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, horizontalAlignConfig["customisation-module-typography-text-alignment-horizontal.right"], rightAlignValue, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentHorizontalAlign('right');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            default:
                console.warn("Invalid horizontal alignment value:", value);
                break;
        }
    }

    const changeVerticalAlignment = (value) => {
        switch (value) {
            case 'top':
                if (verticalAlignConfig?.["customisation-module-typography-text-alignment-vertical.top"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"], verticalAlignConfig["customisation-module-typography-text-alignment-vertical.top"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentVerticalAlign('top');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            case 'middle':
                if (verticalAlignConfig?.["customisation-module-typography-text-alignment-vertical.middle"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"], verticalAlignConfig["customisation-module-typography-text-alignment-vertical.middle"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentVerticalAlign('middle');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            case 'bottom':
                if (verticalAlignConfig?.["customisation-module-typography-text-alignment-vertical.bottom"]?.dataLink) {
                    if (changeStringFieldValue(customisationVars, verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"], verticalAlignConfig["customisation-module-typography-text-alignment-vertical.bottom"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                        // setCurrentVerticalAlign('bottom');
                        props?.forceUpdateComponent?.();
                    }
                }
                break;
            default:
                console.warn("Invalid vertical alignment value:", value);
                break;
        }
    }

    const changeFontWeight = (fieldId, value) => {
        // console.log("Changing font weight to:", value);
        if (value === 'Narrow' && fontWeightConfig["customisation-module-typography-font-weight-narrow"].value === fontWeightConfig["customisation-module-typography-font-weight-narrow"].value) {
            if (changeStringFieldValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-narrow"], fontWeightConfig["customisation-module-typography-font-weight-narrow"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCurrentFontWeight('Narrow');
                props?.forceUpdateComponent?.();
            }
        }
        else if (value === 'Normal' && fontWeightConfig["customisation-module-typography-font-weight-normal"].value === fontWeightConfig["customisation-module-typography-font-weight-normal"].value) {
            if (changeStringFieldValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-normal"], fontWeightConfig["customisation-module-typography-font-weight-normal"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCurrentFontWeight('Normal');
                props?.forceUpdateComponent?.();
            }
        }
        else if (value === 'Bold' && fontWeightConfig["customisation-module-typography-font-weight-bold"].value === fontWeightConfig["customisation-module-typography-font-weight-bold"].value) {
            if (changeStringFieldValue(customisationVars, fontWeightConfig["customisation-module-typography-font-weight-bold"], fontWeightConfig["customisation-module-typography-font-weight-bold"].value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCurrentFontWeight('Bold');
                props?.forceUpdateComponent?.();
            }
        }
    }

    useEffect(() => {
        // Update all the state variables based on the current customisationVars
        if (fontFaceConfig?.dataLink) {
            const fontFaceValue = getCustomisationValue(customisationVars, fontFaceConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setCurrentFontFamily(fontFaceValue || '');
        } else {
            setCurrentFontFamily('');
        }
        if (fontSizeConfig?.dataLink) {
            const fontSizeValue = getCustomisationValue(customisationVars, fontSizeConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setCurrentFontSize(fontSizeValue || '');
        } else {
            setCurrentFontSize('');
        }
        if (fontLineHeightConfig?.dataLink) {
            const fontLineHeightValue = getCustomisationValue(customisationVars, fontLineHeightConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setCurrentFontLineHeight(fontLineHeightValue || 'auto');
        } else {
            setCurrentFontLineHeight('auto');
        }
        /* if (fontColorConfig?.dataLink) {
            const fontColorValue = getCustomisationValue(customisationVars, fontColorConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setCurrentFontColor(fontColorValue || '');
        } else {
            setCurrentFontColor('');
        }   */
        setCurrentFontWeight(findFontWeightValueToDisplay(fontWeightConfig));
    }, [customisationVars, fontFaceConfig, fontSizeConfig, fontLineHeightConfig/* , fontColorConfig */, props.forceUpdate, fontWeightConfig]);

    return (
        <div className="v2-basic-module-holder">
            <h5>Typography</h5>

            {renderFirstCustomBlock(props)}

            <div className='v2-module-columns-holder'>
                <div className='v2-module-one-column v2-full-width'>
                    <div className='v2-module-field-label'>Font</div>
                    <div className='v2-module-columns-holder'>
                        <div className='v2-module-one-column grow v2-font-select-holder'>
                            <ModuleTextInput
                                // fieldLabel="F"
                                value={currentFontFamily}
                                fieldId={'typography-font-face'}
                                enabled={currentFontFamily ? true : false}
                                editable={false}
                                onChange={null}
                                onClick={() => {
                                    slideOutPanelControl.openFontSelector(
                                        currentFontFamily,
                                        (font) => {}, // Preview callback
                                        (font) => { // Confirm callback
                                            changeFontFace(font);
                                        },
                                        'Select Font',
                                        null, // fieldData
                                        campaignContext?.campaignData?.customFonts || [],
                                        campaignContext?.campaignData?._id,
                                        campaignContext?.campaignData
                                    );
                                }}
                                maxLength={100}
                                fakeDropDown={true}
                            />
                        </div>
                        <div className='v2-module-one-column shrink'>
                            <AddButton onClick={() => {
                                slideOutPanelControl.openCustomFonts(
                                    campaignContext?.campaignData?.customFonts || [],
                                    campaignContext?.campaignData?._id,
                                    campaignContext?.campaignData,
                                    'Custom Fonts'
                                );
                            }} />
                        </div>
                    </div>
                    <ModuleTextInput
                        // fieldLabel="B"
                        value={capitalizeFirstLetter(currentFontWeight)}
                        fieldId={'typography-font-weight'}
                        enabled={fontWeightOptions.length > 0}
                        editable={false}
                        onChange={changeFontWeight}
                        hiddenDropDown={true}
                        hiddenDropDownOptions={fontWeightOptions}
                    />
                </div>
            </div>
            <div className='v2-module-columns-holder'>
                <div className='v2-module-one-column'>
                    {/* <div className='v2-module-field-label'>Color</div>
                        <ModuleTextInput
                            fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(currentFontColor, campaignContext.campaignData)}} />}
                            value={currentFontColor}
                            fieldId={'text-color'}
                            enabled={true}
                            editable={false}
                            onChange={null}
                            onClick={
                                //() => {ShowColorPicker(currentFontColor, changeFontColor, campaignContext.campaignData, 'Text Color', () => {}, () => {})}
                                () => {
                                    slideOutPanelControl.openColourPicker(currentFontColor, (c) => changeFontColor(c, false), (c) => changeFontColor(c, true), `Text Color`, fontColorConfig);
                                    setPrevFontColorValue(currentFontColor);
                                }
                            }
                            maxLength={7}
                        /> */}
                        <ColorPickerField
                            fieldData={fontColorConfig}
                            customisationVars={customisationVars}
                            index={props.index}
                            visualisationComponentKey={props.visualisationComponentKey}
                            selectedFeatureNode={props.selectedFeatureNode}
                            colourPickerTitle={'Text Color'}
                            fieldTitle={'Color'}
                            forceUpdateComponent={props.forceUpdateComponent}
                        />
                </div>
                <div className='v2-module-one-column' />
            </div>
            <div className='v2-module-columns-holder'>
                <div className='v2-module-one-column'>
                    <div className={`v2-module-field-label${!fontSizeConfig?.dataLink ? ' disabled' : ''}`}>Size</div>
                    <ModuleNumericalInput
                        // fieldLabel="S"
                        value={currentFontSize}
                        fieldId={'typography-font-size'}
                        enabled={!!fontSizeConfig?.dataLink}
                        editable={true}
                        onChange={changeFontSize}
                        onClick={null}
                        hiddenDropDown={true}
                        hiddenDropDownOptions={[8, 10, 11, 12, 13, 14, 15, 16, 20, 24, 32, 36, 40, 48, 56, 64, 72, 96, 128]}
                    />
                </div>
                <div className='v2-module-one-column'>
                    <div className='v2-module-field-label disabled'>Line Height</div>
                    <ModuleNumericalInput
                        fieldLabel={<div className={`v2-module-text-lineheight-icon`} />}
                        value={typeof currentFontLineHeight === 'string' ? capitalizeFirstLetter(currentFontLineHeight) : currentFontLineHeight}
                        fieldId={'typography-font-line-height'}
                        enabled={!!fontLineHeightConfig?.dataLink}
                        editable={false}
                        onChange={null}
                        hiddenDropDown={true}
                        hiddenDropDownOptions={[{label: 'Auto', value: 'auto'}, 8, 10, 11, 12, 13, 14, 15, 16, 20, 24, 32, 36, 40, 48, 56, 64, 72, 96]}
                    />
                </div>
            </div>
            <div className='v2-module-columns-holder'>
                <div className='v2-module-one-column'>
                    <div className={`v2-module-field-label${(!horizontalAlignmentAvailable && !verticalAlignmentAvailable) ? ' disabled' : ''}`}>Alignment</div>
                    <div className={`v2-module-square-button-group${(!horizontalAlignmentAvailable) ? ' v2-module-disabled-field' : ''}`}>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(leftAlignAvailable && currentHorizontalAlign === 'left') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!leftAlignAvailable && horizontalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeHorizontalAlignment('left')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.textLeftAlign} alt='' />
                            </div>
                        </div>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(centerAlignAvailable && currentHorizontalAlign === 'center') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!centerAlignAvailable && horizontalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeHorizontalAlignment('center')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.textCenterAlign} alt='' />
                            </div>
                        </div>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(rightAlignAvailable && currentHorizontalAlign === 'right') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!rightAlignAvailable && horizontalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeHorizontalAlignment('right')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.textRightAlign} alt='' />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='v2-module-one-column'>
                    <div className='v2-module-field-label'>&nbsp;</div>
                    <div className={`v2-module-square-button-group${(!verticalAlignmentAvailable) ? ' v2-module-disabled-field' : ''}`}>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(topAlignAvailable && currentVerticalAlign === 'top') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!topAlignAvailable && verticalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeVerticalAlignment('top')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.alignTop} alt='' />
                            </div>
                        </div>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(middleAlignAvailable && currentVerticalAlign === 'middle') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!middleAlignAvailable && verticalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeVerticalAlignment('middle')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.alignCenter} alt='' />
                            </div>
                        </div>
                        <div className={`v2-module-square-button-holder`}>
                            <div className={`v2-module-square-button${(bottomAlignAvailable && currentVerticalAlign === 'bottom') ? ' v2-module-button__active v2-no-mouse-events' : ''}${!bottomAlignAvailable && verticalAlignmentAvailable ? ' v2-module-disabled-field' : ''}`}
                                onClick={() => changeVerticalAlignment('bottom')}
                            >
                                <img className={`v2-module-text-align-icon`} src={WORKSPACE_MODULE_ICONS.alignBottom} alt='' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {renderSecondCustomBlock(props)}

        </div>
    )

};

export default TypographyModule;
